import { useCallback, useState } from 'react';
import * as Cesium from 'cesium';

/**
 * Hook for managing display mode (pointcloud/gaussian) and view mode (full/split).
 * @param {React.RefObject} viewerRef - Reference to the Cesium viewer
 * @param {React.RefObject} tilesetRef - Reference to the point cloud tileset
 * @param {React.RefObject} gaussianTilesetRef - Reference to the gaussian splatting tileset
 */
export const useViewModes = (viewerRef, tilesetRef, gaussianTilesetRef) => {
  const [currentDisplayMode, setCurrentDisplayMode] = useState('pointcloud');
  const [currentViewMode, setCurrentViewMode] = useState('full');
  const [splitPosition, setSplitPosition] = useState(0.5);

  const switchViewMode = useCallback((mode) => {
    const viewer = viewerRef.current;
    const pointCloudTileset = tilesetRef.current;
    const gaussianTileset = gaussianTilesetRef.current;

    if (!viewer) {
      console.warn('Viewer not available for view mode switch');
      return;
    }

    if (mode === currentViewMode) {
      return;
    }

    try {
      if (mode === 'split') {
        if (pointCloudTileset && gaussianTileset) {
          pointCloudTileset.show = true;
          gaussianTileset.show = true;

          pointCloudTileset.splitDirection = Cesium.SplitDirection.LEFT;
          gaussianTileset.splitDirection = Cesium.SplitDirection.RIGHT;

          viewer.scene.splitPosition = splitPosition;

          console.log('Enabled split screen mode');
        } else {
          console.warn('Both tilesets required for split screen mode');
          return;
        }
      } else if (mode === 'full') {
        if (pointCloudTileset) {
          pointCloudTileset.splitDirection = Cesium.SplitDirection.NONE;
        }
        if (gaussianTileset) {
          gaussianTileset.splitDirection = Cesium.SplitDirection.NONE;
        }

        if (currentDisplayMode === 'pointcloud') {
          if (gaussianTileset) {
            gaussianTileset.show = false;
          }
          if (pointCloudTileset) {
            pointCloudTileset.show = true;
          }
        } else if (currentDisplayMode === 'gaussian') {
          if (pointCloudTileset) {
            pointCloudTileset.show = false;
          }
          if (gaussianTileset) {
            gaussianTileset.show = true;
          }
        }

        console.log('Enabled full screen mode');
      }

      setCurrentViewMode(mode);
      console.log(`Switched view mode to: ${mode}`);
    } catch (error) {
      console.error('Error switching view mode:', error);
    }
  }, [viewerRef, tilesetRef, gaussianTilesetRef, currentViewMode, splitPosition, currentDisplayMode]);

  const switchDisplayMode = useCallback((mode) => {
    const viewer = viewerRef.current;
    const pointCloudTileset = tilesetRef.current;
    const gaussianTileset = gaussianTilesetRef.current;

    if (!viewer) {
      console.warn('Viewer not available for display mode switch');
      return;
    }

    if (mode === currentDisplayMode) {
      return;
    }

    try {
      if (currentViewMode === 'full') {
        if (mode === 'pointcloud') {
          if (gaussianTileset) {
            gaussianTileset.show = false;
          }
          if (pointCloudTileset) {
            pointCloudTileset.show = true;
          }
        } else if (mode === 'gaussian') {
          if (pointCloudTileset) {
            pointCloudTileset.show = false;
          }
          if (gaussianTileset) {
            gaussianTileset.show = true;
          } else {
            console.warn('Gaussian splatting tileset not available');
            return;
          }
        }
      }

      setCurrentDisplayMode(mode);
      console.log(`Switched display mode to: ${mode}`);
    } catch (error) {
      console.error('Error switching display mode:', error);
    }
  }, [viewerRef, tilesetRef, gaussianTilesetRef, currentDisplayMode, currentViewMode]);

  const updateSplitPosition = useCallback((position) => {
    const viewer = viewerRef.current;

    if (!viewer) {
      console.warn('Viewer not available for split position update');
      return;
    }

    const clampedPosition = Math.max(0.0, Math.min(1.0, position));
    setSplitPosition(clampedPosition);

    if (currentViewMode === 'split') {
      viewer.scene.splitPosition = clampedPosition;
    }
  }, [viewerRef, currentViewMode]);

  return {
    currentDisplayMode,
    setCurrentDisplayMode,
    currentViewMode,
    setCurrentViewMode,
    splitPosition,
    setSplitPosition,
    switchDisplayMode,
    switchViewMode,
    updateSplitPosition,
  };
};
